/**
 * QuestLogPanel — Quest log + fame badge overlay for Spellroads.
 *
 * Layout (desktop/tablet landscape):
 *   [QUEST LOG]  [Fame: Respected badge]  [Close]
 *   ────────────────────────────────────────────────
 *   [Quest List Sidebar] | [Quest Detail Pane]
 *
 * On mobile portrait the sidebar becomes a horizontal scrolling tab
 * row above the detail pane.
 *
 * Integrates with QuestSystem and FameSystem when available; falls back
 * to hardcoded placeholder data so the panel looks functional for testing.
 *
 * Opened/closed with `J` or `Escape`. Accessible via keyboard + gamepad.
 *
 * @module QuestLogPanel
 */

import { UIPanel } from './UIPanel.js';
import DebugLog from '../debug/DebugLog.js';
import EventBus from '../core/EventBus.js';

// ─── Style injection ──────────────────────────────────────────────────────────

const STYLE_ID = 'spellroads-questlog-style';

function injectStyles() {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    /* ══════════════════════════════════════════════════════════════
       QuestLogPanel — Spellroads
       ══════════════════════════════════════════════════════════════ */

    #panel-questlog {
      position: fixed;
      inset: 0;
      z-index: 215;
      display: none;
      flex-direction: column;
      background: rgba(8, 6, 4, 0.97);
      pointer-events: all;
      overflow: hidden;
      font-family: 'Georgia', 'Times New Roman', serif;
      color: #e8d5b0;
    }

    #panel-questlog.visible { display: flex; }

    /* ── Header ──────────────────────────────────────────────────── */
    .ql-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: clamp(8px, 1.5vh, 14px) clamp(12px, 2vw, 24px);
      background: rgba(0, 0, 0, 0.55);
      border-bottom: 1px solid rgba(200, 160, 32, 0.25);
      flex-shrink: 0;
      gap: 12px;
    }

    .ql-header-left {
      display: flex;
      align-items: center;
      gap: 14px;
    }

    .ql-title {
      font-size: clamp(0.9rem, 1.8vw, 1.2rem);
      font-weight: 700;
      color: #c8a020;
      letter-spacing: 0.08em;
      text-transform: uppercase;
    }

    /* Fame badge — color-coded by tier */
    .ql-fame-badge {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      font-size: clamp(0.6rem, 0.95vw, 0.74rem);
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.07em;
      padding: 3px 9px;
      border-radius: 3px;
      border: 1px solid;
      white-space: nowrap;
    }

    /* Tier colours */
    .ql-fame-badge.fame-villain    { color: #c83020; border-color: rgba(200,48,32,0.5);  background: rgba(200,48,32,0.1); }
    .ql-fame-badge.fame-notorious  { color: #a09070; border-color: rgba(160,144,112,0.4); background: rgba(160,144,112,0.08); }
    .ql-fame-badge.fame-unknown    { color: #e8d5b0; border-color: rgba(232,213,176,0.3); background: rgba(232,213,176,0.06); }
    .ql-fame-badge.fame-known      { color: #2080c0; border-color: rgba(32,128,192,0.45); background: rgba(32,128,192,0.1); }
    .ql-fame-badge.fame-respected  { color: #40a060; border-color: rgba(64,160,96,0.45);  background: rgba(64,160,96,0.1); }
    .ql-fame-badge.fame-legendary  { color: #c8a020; border-color: rgba(200,160,32,0.5);  background: rgba(200,160,32,0.1); }

    .ql-close {
      background: none;
      border: 1px solid rgba(200, 160, 32, 0.3);
      color: #a09070;
      cursor: pointer;
      width: 44px;
      height: 44px;
      min-width: 44px;
      min-height: 44px;
      border-radius: 4px;
      font-size: 1.2rem;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: color 0.15s, border-color 0.15s;
      flex-shrink: 0;
    }

    .ql-close:hover,
    .ql-close:focus-visible {
      color: #c8a020;
      border-color: #c8a020;
      outline: none;
    }

    /* ── Body ────────────────────────────────────────────────────── */
    .ql-body {
      flex: 1;
      display: grid;
      grid-template-columns: 220px 1fr;
      min-height: 0;
    }

    /* ── Sidebar ─────────────────────────────────────────────────── */
    .ql-sidebar {
      display: flex;
      flex-direction: column;
      border-right: 1px solid rgba(200, 160, 32, 0.15);
      overflow-y: auto;
      background: rgba(0, 0, 0, 0.25);
      padding: 8px 0;
    }

    .ql-section-heading {
      font-size: clamp(0.6rem, 0.9vw, 0.7rem);
      font-weight: 700;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: #706050;
      padding: 10px 14px 4px;
    }

    .ql-section-heading:first-child {
      padding-top: 6px;
    }

    .ql-section-divider {
      height: 1px;
      background: rgba(200, 160, 32, 0.12);
      margin: 4px 14px;
    }

    .ql-quest-btn {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 1px;
      width: 100%;
      background: none;
      border: none;
      border-left: 3px solid transparent;
      padding: 8px 14px 8px 11px;
      cursor: pointer;
      text-align: left;
      min-height: 44px;
      transition: background 0.12s, border-color 0.12s;
      color: #a09070;
    }

    .ql-quest-btn:hover {
      background: rgba(200, 160, 32, 0.07);
      color: #c8d0a0;
    }

    .ql-quest-btn:focus-visible {
      outline: 2px solid #e8c040;
      outline-offset: -2px;
    }

    .ql-quest-btn.active {
      background: rgba(200, 160, 32, 0.1);
      border-left-color: #c8a020;
      color: #e8d5b0;
    }

    .ql-quest-btn.completed {
      color: #706050;
    }

    .ql-quest-btn.completed.active {
      border-left-color: #706050;
      color: #a09070;
    }

    .ql-quest-btn-name {
      font-size: clamp(0.7rem, 1.1vw, 0.8rem);
      font-weight: 600;
      line-height: 1.3;
    }

    .ql-quest-btn-loc {
      font-size: clamp(0.58rem, 0.85vw, 0.68rem);
      color: #706050;
    }

    /* ── Detail pane ─────────────────────────────────────────────── */
    .ql-detail {
      flex: 1;
      display: flex;
      flex-direction: column;
      overflow-y: auto;
      padding: clamp(14px, 2vh, 24px) clamp(16px, 2.5vw, 32px);
    }

    .ql-detail-empty {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #706050;
      font-style: italic;
      font-size: clamp(0.8rem, 1.2vw, 0.95rem);
    }

    .ql-detail-title {
      font-size: clamp(1rem, 1.8vw, 1.3rem);
      font-weight: 700;
      color: #e8d5b0;
      margin-bottom: 6px;
      line-height: 1.2;
    }

    .ql-detail-meta {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 16px;
      flex-wrap: wrap;
    }

    .ql-type-badge {
      font-size: clamp(0.58rem, 0.85vw, 0.68rem);
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      padding: 2px 7px;
      border-radius: 3px;
      border: 1px solid;
    }

    .ql-type-badge.type-main   { color: #c8a020; border-color: rgba(200,160,32,0.4); background: rgba(200,160,32,0.1); }
    .ql-type-badge.type-side   { color: #2080c0; border-color: rgba(32,128,192,0.4); background: rgba(32,128,192,0.1); }
    .ql-type-badge.type-bounty { color: #c83020; border-color: rgba(200,48,32,0.4);  background: rgba(200,48,32,0.1); }
    .ql-type-badge.type-errand { color: #a09070; border-color: rgba(160,144,112,0.3); background: rgba(0,0,0,0.15); }

    .ql-location-tag {
      font-size: clamp(0.62rem, 0.9vw, 0.72rem);
      color: #a09070;
    }

    .ql-description {
      font-size: clamp(0.78rem, 1.2vw, 0.9rem);
      line-height: 1.65;
      color: #c8b890;
      margin-bottom: 20px;
    }

    .ql-section-label {
      font-size: clamp(0.62rem, 0.9vw, 0.72rem);
      font-weight: 700;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: #706050;
      margin-bottom: 8px;
      margin-top: 18px;
    }

    .ql-section-label:first-of-type {
      margin-top: 0;
    }

    .ql-objective-list {
      list-style: none;
      display: flex;
      flex-direction: column;
      gap: 5px;
      margin-bottom: 4px;
    }

    .ql-objective-item {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: clamp(0.74rem, 1.1vw, 0.85rem);
      color: #a09070;
      line-height: 1.4;
    }

    .ql-objective-item.done {
      color: #40a060;
    }

    .ql-obj-icon {
      flex-shrink: 0;
      width: 16px;
      text-align: center;
      font-size: 0.85em;
    }

    .ql-obj-progress {
      margin-left: auto;
      font-size: clamp(0.6rem, 0.85vw, 0.7rem);
      color: #706050;
      white-space: nowrap;
    }

    .ql-objective-item.done .ql-obj-progress {
      color: #40a060;
    }

    /* ── Rewards ─────────────────────────────────────────────────── */
    .ql-rewards-list {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      margin-top: 4px;
    }

    .ql-reward-chip {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      font-size: clamp(0.68rem, 1.0vw, 0.8rem);
      color: #c8b890;
      background: rgba(255, 255, 255, 0.04);
      border: 1px solid rgba(200, 160, 32, 0.2);
      border-radius: 3px;
      padding: 4px 9px;
    }

    .ql-reward-icon {
      font-size: 1em;
    }

    /* ── No quests placeholder ───────────────────────────────────── */
    .ql-no-quests {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      flex: 1;
      gap: 8px;
      color: #706050;
      font-size: clamp(0.8rem, 1.2vw, 0.95rem);
      font-style: italic;
      padding: 40px;
      text-align: center;
    }

    /* ══════════════════════════════════════════════════════════════
       Quest Indicators — '!' bubbles for map elements
       ══════════════════════════════════════════════════════════════ */
    .quest-indicator {
      position: absolute;
      top: -10px;
      right: -8px;
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: #c8a020;
      border: 2px solid #e8d5b0;
      color: #1a1510;
      font-size: 12px;
      font-weight: 900;
      font-family: 'Georgia', serif;
      display: flex;
      align-items: center;
      justify-content: center;
      pointer-events: none;
      z-index: 10;
      box-shadow: 0 2px 6px rgba(0,0,0,0.6);
      animation: quest-bounce 1.6s ease-in-out infinite;
    }

    @keyframes quest-bounce {
      0%, 100% { transform: translateY(0); }
      50%       { transform: translateY(-3px); }
    }

    /* ══════════════════════════════════════════════════════════════
       Responsive — Mobile portrait: sidebar becomes scrolling tab row
       ══════════════════════════════════════════════════════════════ */
    @media (max-width: 767px) and (orientation: portrait) {
      .ql-body {
        grid-template-columns: 1fr;
        grid-template-rows: auto 1fr;
      }

      .ql-sidebar {
        flex-direction: row;
        flex-wrap: nowrap;
        overflow-x: auto;
        overflow-y: hidden;
        border-right: none;
        border-bottom: 1px solid rgba(200, 160, 32, 0.15);
        padding: 6px 8px;
        gap: 4px;
        max-height: none;
        scrollbar-width: thin;
        align-items: stretch;
      }

      .ql-section-heading,
      .ql-section-divider {
        display: none;
      }

      .ql-quest-btn {
        flex-direction: row;
        align-items: center;
        border-left: none;
        border-bottom: 3px solid transparent;
        padding: 6px 10px;
        min-width: 100px;
        white-space: nowrap;
        border-radius: 4px 4px 0 0;
        gap: 6px;
      }

      .ql-quest-btn.active {
        border-left-color: transparent;
        border-bottom-color: #c8a020;
        background: rgba(200, 160, 32, 0.1);
      }

      .ql-quest-btn.completed.active {
        border-bottom-color: #706050;
      }

      .ql-quest-btn-loc {
        display: none;
      }

      .ql-detail {
        padding: 12px;
      }
    }

    /* Mobile landscape: narrower sidebar */
    @media (max-width: 1023px) and (orientation: landscape) {
      .ql-body {
        grid-template-columns: 180px 1fr;
      }

      .ql-quest-btn {
        padding: 7px 10px;
      }
    }
  `;
  document.head.appendChild(style);
}

// ─── Fame tier helpers ────────────────────────────────────────────────────────

/**
 * Maps a fame score (or tier name string) to display data.
 * @param {number|string} fameOrTier
 * @returns {{ tier: string, label: string, cssClass: string }}
 */
function resolveFameTier(fameOrTier) {
  const tierMap = {
    villain:   { label: 'Villain',    cssClass: 'fame-villain' },
    notorious: { label: 'Notorious',  cssClass: 'fame-notorious' },
    unknown:   { label: 'Unknown',    cssClass: 'fame-unknown' },
    known:     { label: 'Known',      cssClass: 'fame-known' },
    respected: { label: 'Respected',  cssClass: 'fame-respected' },
    legendary: { label: 'Legendary',  cssClass: 'fame-legendary' },
  };

  if (typeof fameOrTier === 'string' && tierMap[fameOrTier]) {
    return { tier: fameOrTier, ...tierMap[fameOrTier] };
  }

  // Numeric fame score
  const score = typeof fameOrTier === 'number' ? fameOrTier : 0;
  let tier;
  if      (score <= -500) tier = 'villain';
  else if (score < -100)  tier = 'notorious';
  else if (score <  100)  tier = 'unknown';
  else if (score <  300)  tier = 'known';
  else if (score <  700)  tier = 'respected';
  else                    tier = 'legendary';

  return { tier, ...tierMap[tier] };
}

// ─── Placeholder data (when QuestSystem not yet loaded) ───────────────────────

const PLACEHOLDER_QUESTS = [
  {
    id:          'quest_wolf_den',
    title:       'The Wolf Den',
    type:        'bounty',
    location:    'Ironmoor Valley',
    description: 'A pack of dire wolves has been preying on local farms. Elder Gunnar asks you to clear their den in the hills north of Stonehaven and return with proof of the kill.',
    objectives:  [
      { text: 'Kill dire wolves', current: 5, total: 5, done: true },
      { text: 'Return to Elder Gunnar', current: 0, total: 1, done: false },
    ],
    rewards: [
      { icon: '\uD83D\uDCB0', label: '50 gold' },
      { icon: '\uD83C\uDF7A', label: '2x Mead' },
    ],
    completed: false,
  },
  {
    id:          'quest_deliver_goods',
    title:       'Deliver the Goods',
    type:        'errand',
    location:    'Thornwall',
    description: 'Merchant Hilde needs her supply shipment delivered to the outpost at Thornwall before the first snow. The road is said to be watched by brigands.',
    objectives:  [
      { text: 'Collect shipment from Hilde', current: 1, total: 1, done: true },
      { text: 'Deliver to Thornwall outpost', current: 0, total: 1, done: false },
    ],
    rewards: [
      { icon: '\uD83D\uDCB0', label: '30 gold' },
    ],
    completed: false,
  },
  {
    id:          'quest_lost_cargo',
    title:       'Lost Cargo',
    type:        'side',
    location:    'Old South Road',
    description: 'A trading caravan lost several crates when their cart overturned on the Old South Road. The cargo may still be scattered near the Frost Bridge.',
    objectives:  [
      { text: 'Find the lost crates', current: 2, total: 4, done: false },
    ],
    rewards: [
      { icon: '\uD83D\uDCB0', label: '20 gold' },
      { icon: '\uD83E\uDDEA', label: '1x Remedy Potion' },
    ],
    completed: false,
  },
  {
    id:          'quest_old_promise',
    title:       'An Old Promise',
    type:        'main',
    location:    'Ashveil Ruins',
    description: 'You swore to bring your fallen companion\'s axe back to his family in the Ashveil Ruins. The ruins have since been overtaken by a wandering bandit clan.',
    objectives:  [
      { text: 'Retrieve Bjorn\'s axe', current: 1, total: 1, done: true },
      { text: 'Clear the ruins of bandits', current: 5, total: 5, done: true },
      { text: 'Deliver the axe to Bjorn\'s kin', current: 1, total: 1, done: true },
    ],
    rewards: [
      { icon: '\uD83D\uDCB0', label: '10 gold' },
      { icon: '\u2764\uFE0F',  label: 'Loyalty' },
    ],
    completed: true,
  },
];

// ─── QuestLogPanel ────────────────────────────────────────────────────────────

class QuestLogPanel extends UIPanel {
  constructor() {
    injectStyles();
    super('panel-questlog', { trapFocus: false, zLayer: 'panel' });

    /** @type {string|null} Currently selected quest id */
    this._selectedId = null;

    /** @type {Array} Cached quest list from last open */
    this._quests = [];

    /** @type {Function|null} EventBus unsub handle */
    this._unsub = null;
  }

  // ─── UIPanel lifecycle ───────────────────────────────────────────────────

  show() {
    this._quests = this._loadQuests();

    // Auto-select first active quest (or first quest overall)
    const firstActive = this._quests.find(q => !q.completed);
    const firstAny    = this._quests[0];
    if (!this._selectedId || !this._quests.find(q => q.id === this._selectedId)) {
      this._selectedId = (firstActive ?? firstAny)?.id ?? null;
    }

    this._render();
    super.show();
    this._bindEvents();

    DebugLog.info('[quest] QuestLogPanel opened');
  }

  hide() {
    super.hide();
    DebugLog.info('[quest] QuestLogPanel closed');
  }

  destroy() {
    if (this._unsub) { this._unsub(); this._unsub = null; }
    super.destroy();
  }

  // ─── Data loading ────────────────────────────────────────────────────────

  /**
   * Load quests from QuestSystem if available, otherwise use placeholders.
   * @returns {Array}
   */
  _loadQuests() {
    try {
      // Dynamic import guard — QuestSystem may not be loaded yet
      const qs = window.__questSystem;
      if (qs && typeof qs.getActive === 'function') {
        const active    = qs.getActive() ?? [];
        const completed = (typeof qs.getCompleted === 'function' ? qs.getCompleted() : []) ?? [];
        const all       = [...active, ...completed];
        if (all.length > 0) return all;
      }
    } catch (e) {
      DebugLog.warn(`[quest] QuestSystem not available, using placeholders: ${e.message}`);
    }
    return PLACEHOLDER_QUESTS;
  }

  /**
   * Get current fame tier from FameSystem or return a default.
   * @returns {{ tier: string, label: string, cssClass: string }}
   */
  _loadFame() {
    try {
      const fs = window.__fameSystem;
      if (fs && typeof fs.getTier === 'function') {
        return resolveFameTier(fs.getTier());
      }
      if (fs && typeof fs.getFame === 'function') {
        return resolveFameTier(fs.getFame());
      }
    } catch (e) {
      DebugLog.warn(`[quest] FameSystem not available: ${e.message}`);
    }
    // Default fallback for testing
    return resolveFameTier('respected');
  }

  // ─── DOM build ───────────────────────────────────────────────────────────

  _buildHTML() {
    return `
      <div class="ql-header">
        <div class="ql-header-left">
          <div class="ql-title">Quest Log</div>
          <div class="ql-fame-badge fame-respected" id="ql-fame-badge">
            <span id="ql-fame-icon">&#9733;</span>
            <span id="ql-fame-label">Respected</span>
          </div>
        </div>
        <button class="ql-close" id="ql-close-btn" aria-label="Close quest log" tabindex="0">&times;</button>
      </div>
      <div class="ql-body">
        <nav class="ql-sidebar" id="ql-sidebar" role="navigation" aria-label="Quest list">
          <div id="ql-quest-list"></div>
        </nav>
        <div class="ql-detail" id="ql-detail" role="region" aria-label="Quest detail" aria-live="polite"></div>
      </div>
    `;
  }

  // ─── Full render ──────────────────────────────────────────────────────────

  _render() {
    this._renderFameBadge();
    this._renderQuestList();
    this._renderDetail();
  }

  // ─── Fame badge ───────────────────────────────────────────────────────────

  _renderFameBadge() {
    const badge     = this.el.querySelector('#ql-fame-badge');
    const labelEl   = this.el.querySelector('#ql-fame-label');
    if (!badge || !labelEl) return;

    const fame = this._loadFame();

    // Remove all tier classes and apply the current one
    badge.className = `ql-fame-badge ${fame.cssClass}`;
    labelEl.textContent = `Fame: ${fame.label}`;
  }

  // ─── Sidebar quest list ───────────────────────────────────────────────────

  _renderQuestList() {
    const list = this.el.querySelector('#ql-quest-list');
    if (!list) return;
    list.innerHTML = '';

    const active    = this._quests.filter(q => !q.completed);
    const completed = this._quests.filter(q => q.completed);

    if (this._quests.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'ql-section-heading';
      empty.textContent = 'No active quests';
      list.appendChild(empty);
      return;
    }

    // Active section
    if (active.length > 0) {
      list.appendChild(this._makeSectionHeading(`Active (${active.length})`));
      list.appendChild(this._makeSectionDivider());
      for (const quest of active) {
        list.appendChild(this._makeQuestBtn(quest));
      }
    }

    // Completed section
    if (completed.length > 0) {
      list.appendChild(this._makeSectionHeading('Completed'));
      list.appendChild(this._makeSectionDivider());
      for (const quest of completed) {
        list.appendChild(this._makeQuestBtn(quest));
      }
    }
  }

  /**
   * @param {string} text
   * @returns {HTMLElement}
   */
  _makeSectionHeading(text) {
    const el = document.createElement('div');
    el.className = 'ql-section-heading';
    el.textContent = text;
    return el;
  }

  /**
   * @returns {HTMLElement}
   */
  _makeSectionDivider() {
    const el = document.createElement('div');
    el.className = 'ql-section-divider';
    return el;
  }

  /**
   * Build a sidebar quest button.
   * @param {object} quest
   * @returns {HTMLButtonElement}
   */
  _makeQuestBtn(quest) {
    const isActive = quest.id === this._selectedId;
    const btn = document.createElement('button');
    btn.className = [
      'ql-quest-btn',
      quest.completed ? 'completed' : '',
      isActive ? 'active' : '',
    ].filter(Boolean).join(' ');
    btn.dataset.questId = quest.id;
    btn.tabIndex = 0;
    btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    btn.setAttribute('aria-label', quest.title);

    const nameEl = document.createElement('span');
    nameEl.className = 'ql-quest-btn-name';
    nameEl.textContent = quest.title;

    const locEl = document.createElement('span');
    locEl.className = 'ql-quest-btn-loc';
    locEl.textContent = quest.location ?? '';

    btn.appendChild(nameEl);
    if (quest.location) btn.appendChild(locEl);

    btn.addEventListener('click', () => this._selectQuest(quest.id));
    return btn;
  }

  // ─── Detail pane ──────────────────────────────────────────────────────────

  _renderDetail() {
    const pane = this.el.querySelector('#ql-detail');
    if (!pane) return;

    if (!this._selectedId) {
      pane.innerHTML = '<div class="ql-detail-empty">Select a quest to view details.</div>';
      return;
    }

    const quest = this._quests.find(q => q.id === this._selectedId);
    if (!quest) {
      pane.innerHTML = '<div class="ql-detail-empty">Quest not found.</div>';
      return;
    }

    // ── Title + meta ─────────────────────────────────────────────
    const titleEl = document.createElement('div');
    titleEl.className = 'ql-detail-title';
    titleEl.textContent = quest.title;

    const metaEl = document.createElement('div');
    metaEl.className = 'ql-detail-meta';

    const typeBadge = document.createElement('span');
    typeBadge.className = `ql-type-badge type-${quest.type ?? 'side'}`;
    typeBadge.textContent = this._typeLabel(quest.type);
    metaEl.appendChild(typeBadge);

    if (quest.location) {
      const locTag = document.createElement('span');
      locTag.className = 'ql-location-tag';
      locTag.textContent = quest.location;
      metaEl.appendChild(locTag);
    }

    // ── Description ──────────────────────────────────────────────
    const descEl = document.createElement('p');
    descEl.className = 'ql-description';
    descEl.textContent = quest.description ?? '';

    // ── Objectives ───────────────────────────────────────────────
    const objLabel = document.createElement('div');
    objLabel.className = 'ql-section-label';
    objLabel.textContent = 'Objectives';

    const objList = document.createElement('ul');
    objList.className = 'ql-objective-list';

    const objectives = quest.objectives ?? [];
    for (const obj of objectives) {
      objList.appendChild(this._makeObjectiveItem(obj));
    }

    if (objectives.length === 0) {
      const none = document.createElement('li');
      none.className = 'ql-objective-item';
      none.textContent = 'No objectives listed.';
      objList.appendChild(none);
    }

    // ── Rewards ──────────────────────────────────────────────────
    const rewardLabel = document.createElement('div');
    rewardLabel.className = 'ql-section-label';
    rewardLabel.textContent = 'Rewards';

    const rewardList = document.createElement('div');
    rewardList.className = 'ql-rewards-list';

    const rewards = quest.rewards ?? [];
    for (const reward of rewards) {
      rewardList.appendChild(this._makeRewardChip(reward));
    }

    if (rewards.length === 0) {
      const none = document.createElement('span');
      none.className = 'ql-reward-chip';
      none.textContent = 'None listed';
      rewardList.appendChild(none);
    }

    // ── Assemble ─────────────────────────────────────────────────
    pane.innerHTML = '';
    pane.appendChild(titleEl);
    pane.appendChild(metaEl);
    pane.appendChild(descEl);
    pane.appendChild(objLabel);
    pane.appendChild(objList);
    pane.appendChild(rewardLabel);
    pane.appendChild(rewardList);
  }

  /**
   * Build a single objective list item.
   * @param {{ text: string, current: number, total: number, done: boolean }} obj
   * @returns {HTMLLIElement}
   */
  _makeObjectiveItem(obj) {
    const li = document.createElement('li');
    li.className = `ql-objective-item${obj.done ? ' done' : ''}`;

    const icon = document.createElement('span');
    icon.className = 'ql-obj-icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.textContent = obj.done ? '\u2713' : '\u25CB';

    const text = document.createElement('span');
    text.textContent = obj.text;

    li.appendChild(icon);
    li.appendChild(text);

    // Progress indicator (only when not a simple boolean completion)
    if (typeof obj.total === 'number' && obj.total > 1) {
      const prog = document.createElement('span');
      prog.className = 'ql-obj-progress';
      prog.textContent = `${obj.current ?? 0}/${obj.total}`;
      li.appendChild(prog);
    }

    return li;
  }

  /**
   * Build a single reward chip.
   * @param {{ icon: string, label: string }} reward
   * @returns {HTMLElement}
   */
  _makeRewardChip(reward) {
    const chip = document.createElement('span');
    chip.className = 'ql-reward-chip';

    const iconEl = document.createElement('span');
    iconEl.className = 'ql-reward-icon';
    iconEl.setAttribute('aria-hidden', 'true');
    iconEl.textContent = reward.icon ?? '';

    chip.appendChild(iconEl);
    chip.appendChild(document.createTextNode(reward.label ?? ''));
    return chip;
  }

  /**
   * Human-readable label for a quest type string.
   * @param {string} type
   * @returns {string}
   */
  _typeLabel(type) {
    const labels = {
      main:   'Main Quest',
      side:   'Side Quest',
      bounty: 'Bounty',
      errand: 'Errand',
    };
    return labels[type] ?? 'Quest';
  }

  // ─── Quest selection ──────────────────────────────────────────────────────

  /**
   * Select a quest by id, update sidebar highlights and re-render detail.
   * @param {string} questId
   */
  _selectQuest(questId) {
    this._selectedId = questId;

    // Update sidebar button states
    const buttons = this.el.querySelectorAll('.ql-quest-btn');
    for (const btn of buttons) {
      const isActive = btn.dataset.questId === questId;
      btn.classList.toggle('active', isActive);
      btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    }

    this._renderDetail();

    DebugLog.info(`[quest] Selected quest: ${questId}`);
  }

  // ─── Event binding ────────────────────────────────────────────────────────

  _bindEvents() {
    // Close button
    const closeBtn = this.el.querySelector('#ql-close-btn');
    if (closeBtn && !closeBtn._qlBound) {
      closeBtn.addEventListener('click', () => this.hide());
      closeBtn._qlBound = true;
    }
  }

  // ─── Quest indicator helpers ──────────────────────────────────────────────

  /**
   * Add an animated '!' indicator badge to a map element.
   * Each questId gets its own badge; repeated calls for the same id are no-ops.
   *
   * @param {HTMLElement} mapElement — The element to attach the badge to
   * @param {string}      questId    — Unique quest identifier
   */
  static showQuestIndicator(mapElement, questId) {
    if (!mapElement) return;

    // Ensure the map element is positioned so the absolute badge renders correctly
    const pos = getComputedStyle(mapElement).position;
    if (pos === 'static') {
      mapElement.style.position = 'relative';
    }

    const existing = mapElement.querySelector(`[data-quest-indicator="${questId}"]`);
    if (existing) return; // Already attached

    const badge = document.createElement('span');
    badge.className = 'quest-indicator';
    badge.dataset.questIndicator = questId;
    badge.setAttribute('aria-label', 'Quest available');
    badge.setAttribute('aria-hidden', 'true');
    badge.textContent = '!';

    mapElement.appendChild(badge);
  }

  /**
   * Remove the '!' indicator badge for a specific questId from a map element.
   *
   * @param {HTMLElement} mapElement
   * @param {string}      questId
   */
  static hideQuestIndicator(mapElement, questId) {
    if (!mapElement) return;
    const badge = mapElement.querySelector(`[data-quest-indicator="${questId}"]`);
    if (badge) badge.remove();
  }

  // ─── Public init (singleton pattern) ─────────────────────────────────────

  /**
   * Initialize the panel and register EventBus listeners.
   * Call once from main.js.
   */
  init() {
    // Open when 'quest:open' is emitted (e.g. from debug console or other systems)
    this._unsub = EventBus.on('quest:open', () => {
      this.show();
    });

    DebugLog.info('[quest] QuestLogPanel initialized');
  }
}

// ─── Singleton export ─────────────────────────────────────────────────────────

const questLogPanel = new QuestLogPanel();
export default questLogPanel;
